import React, { useMemo } from 'react'
import { Popover, Typography, Box, CircularProgress, Tooltip, Stack, Divider, IconButton } from '@mui/material'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import LicenseAvatar from 'components/containers/inbound-tickets/components/LicenseAvatar'
import { useGetCustomerQuery } from 'services/customers'
import { Edit, OpenInNew, Warning } from '@mui/icons-material'
import { showFormModal } from 'state/ui'
import FORM_MODALS from 'utils/ui/modals'
import { convertShippingInfoToCityStateZip, dateFormatter, formatAddress } from 'utils/data-manipulation/valueFormatter'
import { isDateInPast } from 'utils/helpers'

const CustomerInfoPopover = ({ selectedCustomer, anchorEl, onClose }) => {
  const {
    data: customerData,
    isLoading,
    isError,
  } = useGetCustomerQuery(
    { customerId: selectedCustomer?.id },
    { refetchOnMountOrArgChange: true, skip: !selectedCustomer?.id },
  )

  const dispatch = useDispatch()

  const formatTimestamp = timestamp => dateFormatter(timestamp)

  const renderContent = useMemo(() => {
    if (isLoading) {
      return (
        <Box p={2}>
          <CircularProgress />
        </Box>
      )
    }

    if (isError) {
      return (
        <Box p={2}>
          <Typography>Error fetching customer details.</Typography>
        </Box>
      )
    }

    return (
      <Box p={2}>
        <Stack sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <LicenseAvatar selectedCustomer={selectedCustomer || null} customerData={customerData} />
          <Box sx={{ marginLeft: '8px' }}>
            <Typography variant="body1">{customerData?.displayName}</Typography>
            <Typography variant="caption">
              {`${formatAddress(customerData?.contacts?.[0])}`}
              <br />
              {`${convertShippingInfoToCityStateZip(customerData?.contacts?.[0])}`}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="column" spacing={1} mt={1}>
          <Typography variant="body2">
            Name: {customerData?.contacts?.[0]?.firstName} {customerData?.contacts?.[0]?.lastName}
          </Typography>
          <Typography variant="body2">License: {customerData?.contacts?.[0]?.licenseNumber || 'None'}</Typography>
          {isDateInPast(customerData?.contacts?.[0]?.licenseExpiration) && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2">
                License Expiration: {formatTimestamp(customerData?.contacts?.[0]?.licenseExpiration)}
              </Typography>
              <Tooltip title="License Expired">
                <Warning color="error" />
              </Tooltip>
            </Stack>
          )}
          <Typography variant="body1" maxWidth="250px" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            Notes:
            <br />
            {customerData?.notes}
          </Typography>
        </Stack>
        <Divider sx={{ marginTop: '8px' }} />
        <Stack direction="row" spacing={1} marginTop={1}>
          <Link to={`/app/customers/${customerData?.id}/account`} target="_blank" style={{ textDecoration: 'none' }}>
            <Tooltip title="View customer">
              <IconButton size="small">
                <OpenInNew />
              </IconButton>
            </Tooltip>
          </Link>
          <Tooltip title="Edit customer">
            <IconButton
              size="small"
              onClick={() => {
                onClose()
                dispatch(
                  showFormModal({
                    formModalName: FORM_MODALS.NEW_CUSTOMER,
                    formModalProps: { customerId: customerData?.id },
                  }),
                )
              }}
            >
              <Edit />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    )
  }, [isLoading, isError, customerData, selectedCustomer, dispatch, onClose])

  return (
    <Popover open={!!anchorEl} anchorEl={anchorEl} onClose={onClose}>
      {renderContent}
    </Popover>
  )
}

CustomerInfoPopover.propTypes = {
  selectedCustomer: PropTypes.object,
  anchorEl: PropTypes.object,
  onClose: PropTypes.func.isRequired,
}

CustomerInfoPopover.defaultProps = {
  anchorEl: null,
  selectedCustomer: null,
}

export default CustomerInfoPopover
